<template>
  <modal
    :name="modalName"
    :width="'92%'"
    :height="'auto'"
    :max-width="980"
    :scrollable="true"
    :adaptive="true"
    :click-to-close="false"
    :reset="true"
    class="trengo-resource-modal modal-not-overflow"
    @opened="focusHeaderClose"
    @before-close="onBeforeClose"
  >
    <article role="dialog" aria-modal="true" aria-labelledby="trengo-resource-title">
      <AppModalHeader
        ref="modalHeader"
        design="new"
        eyebrow="Trengo"
        :title="title"
        title-id="trengo-resource-title"
        @close="close"
      />

      <div v-if="loading" class="trengo-resource-modal__state">
        <app-spinner design="new" />
      </div>
      <div v-else-if="error" class="trengo-resource-modal__state trengo-resource-modal__state--error">
        <strong>{{ error }}</strong>
        <app-button design="new" theme="dark" @click.native="load">Retry</app-button>
      </div>
      <div v-else-if="detail" class="trengo-resource-modal__body">
        <dl class="trengo-resource-modal__fields">
          <div v-for="field in visibleFields" :key="field.key">
            <dt>{{ field.label }}</dt>
            <dd>
              <template v-if="field.kind === 'mapped-user'">
                {{ mappedUserName(detail[field.key]) }}
              </template>
              <template v-else-if="field.kind === 'resource'">
                <button
                  v-if="detail[field.key]"
                  type="button"
                  class="trengo-resource-modal__link"
                  @click="openRelated(field.resource, detail[field.key].id)"
                >
                  {{ resourceName(detail[field.key]) }}
                </button>
                <span v-else>—</span>
              </template>
              <div v-else-if="field.kind === 'resources'" class="trengo-resource-modal__links">
                <button
                  v-for="item in detail[field.key] || []"
                  :key="item.id"
                  type="button"
                  class="trengo-resource-modal__link"
                  @click="openRelated(field.resource, item.id)"
                >
                  {{ resourceName(item) }}
                </button>
                <span v-if="!(detail[field.key] || []).length">—</span>
              </div>
              <template v-else>{{ formatValue(detail[field.key], field.kind) }}</template>
            </dd>
          </div>
        </dl>

        <section class="trengo-resource-modal__payload">
          <h3>Stored source payload</h3>
          <p>The last valid source object saved for this Trengo projection.</p>
          <pre>{{ formattedPayload }}</pre>
        </section>
      </div>
    </article>
  </modal>
</template>

<script>
import { format } from 'date-fns'
import { getTrengoResource } from '@/http'

const COMMON_FIELDS = [
  { key: 'external_id', label: 'Trengo ID' },
  { key: 'active', label: 'Active', kind: 'boolean' },
  { key: 'last_seen_at', label: 'Last seen', kind: 'date' },
  { key: 'provider_created_at', label: 'Created in Trengo', kind: 'date' },
  { key: 'provider_updated_at', label: 'Updated in Trengo', kind: 'date' },
  { key: 'projection_updated_at', label: 'Projection updated', kind: 'date' }
]

const RESOURCE_FIELDS = {
  channels: [
    { key: 'name', label: 'Name' },
    { key: 'display_name', label: 'Display name' },
    { key: 'title', label: 'Title' },
    { key: 'channel_type', label: 'Type' },
    { key: 'status', label: 'Status' },
    { key: 'is_private', label: 'Privacy', kind: 'privacy' },
    { key: 'provider_metadata_synced_at', label: 'Metadata synced', kind: 'date' }
  ],
  users: [
    { key: 'name', label: 'Name' },
    { key: 'mapped_user', label: 'Mapped Board user', kind: 'mapped-user' }
  ],
  labels: [
    { key: 'name', label: 'Name' },
    { key: 'color', label: 'Color' }
  ],
  teams: [{ key: 'name', label: 'Name' }],
  templates: [
    { key: 'title', label: 'Title' },
    { key: 'slug', label: 'Slug' },
    { key: 'category', label: 'Category' },
    { key: 'language', label: 'Language' },
    { key: 'status', label: 'Status' },
    { key: 'message', label: 'Message' },
    { key: 'primary_channel', label: 'Primary channel', kind: 'resource', resource: 'channels' },
    { key: 'channels', label: 'Channels', kind: 'resources', resource: 'channels' }
  ],
  contacts: [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'is_deleted', label: 'Deleted in Trengo', kind: 'boolean' }
  ]
}

export default {
  name: 'TrengoResourceModal',
  props: {
    resource: { type: String, required: true },
    resourceId: { type: Number, required: true }
  },
  data() {
    return {
      modalName: 'trengo-resource-detail',
      detail: null,
      loading: false,
      error: '',
      requestId: 0
    }
  },
  computed: {
    title() {
      if (!this.detail) return 'Resource details'
      return this.resourceName(this.detail)
    },
    visibleFields() {
      return (RESOURCE_FIELDS[this.resource] || []).concat(COMMON_FIELDS)
    },
    formattedPayload() {
      return JSON.stringify(this.detail && this.detail.raw ? this.detail.raw : {}, null, 2)
    }
  },
  mounted() {
    this.$modal.show(this.modalName)
    this.load()
  },
  beforeDestroy() {
    this.requestId += 1
  },
  methods: {
    async load() {
      const requestId = ++this.requestId
      this.loading = true
      this.error = ''
      try {
        const { data } = await getTrengoResource(this.resource, this.resourceId)
        if (requestId === this.requestId) this.detail = data
      } catch (error) {
        if (requestId === this.requestId) this.error = this.errorMessage(error)
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    focusHeaderClose() {
      this.$nextTick(() => {
        if (this.$refs.modalHeader) this.$refs.modalHeader.focusClose()
      })
    },
    close() {
      if (!this._isDestroyed) this.$emit('close')
    },
    onBeforeClose(event) {
      event.cancel()
      this.close()
    },
    openRelated(resource, id) {
      this.$emit('open-related', { resource, id })
    },
    resourceName(item) {
      return item.display_name || item.title || item.name || item.slug || `Trengo #${ item.external_id || item.id }`
    },
    mappedUserName(user) {
      if (!user) return 'Not mapped'
      return user.full_name || user.email || `Board user #${ user.id }`
    },
    formatValue(value, kind) {
      if (kind === 'boolean') return value === true ? 'Yes' : value === false ? 'No' : 'Unknown'
      if (kind === 'privacy') return value === true ? 'Private' : value === false ? 'Shared' : 'Unknown'
      if (kind === 'date') return value ? format(new Date(value), 'dd MMM yyyy, HH:mm') : '—'
      return value === null || value === undefined || value === '' ? '—' : value
    },
    errorMessage(error) {
      const data = error.response && error.response.data
      return (data && (data.detail || data.message)) || 'Unable to load this Trengo resource.'
    }
  }
}
</script>

<style lang="scss" scoped>
.trengo-resource-modal {
  &__body {
    max-height: calc(90vh - 96px);
    padding: 0 $ui-space-24 $ui-space-24;
    overflow-y: auto;
  }

  &__state {
    display: flex;
    min-height: 260px;
    align-items: center;
    justify-content: center;
    gap: $ui-space-16;
  }

  &__state--error {
    color: $ui-red;
  }

  &__fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin: 0;
    border: 1px solid $ui-line;
    border-radius: $ui-card-radius;
    overflow: hidden;

    > div {
      min-width: 0;
      padding: $ui-space-12 $ui-space-16;
      border-bottom: 1px solid $ui-line;

      &:nth-child(odd) {
        border-right: 1px solid $ui-line;
      }
    }

    dt {
      margin-bottom: $ui-space-4;
      color: $ui-muted;
      font-size: $ui-font-size-metadata;
      font-weight: 600;
      text-transform: uppercase;
    }

    dd {
      margin: 0;
      color: $ui-text;
      overflow-wrap: anywhere;
      white-space: pre-wrap;
    }
  }

  &__links {
    display: flex;
    flex-wrap: wrap;
    gap: $ui-space-8;
  }

  &__link {
    padding: 0;
    border: 0;
    background: transparent;
    color: $ui-blue;
    cursor: pointer;
    font: inherit;
    text-align: left;

    &:hover {
      text-decoration: underline;
    }
  }

  &__payload {
    margin-top: $ui-space-24;

    h3 {
      margin: 0;
      color: $ui-ink;
      font-family: $ui-font-heading;
    }

    p {
      margin: $ui-space-4 0 $ui-space-12;
      color: $ui-muted;
    }

    pre {
      max-height: 360px;
      margin: 0;
      padding: $ui-space-16;
      border-radius: $ui-card-radius;
      overflow: auto;
      background: $ui-surface-soft;
      color: $ui-text;
      font-size: 13px;
      line-height: 1.5;
    }
  }
}

@media (max-width: 720px) {
  .trengo-resource-modal__fields {
    grid-template-columns: 1fr;

    > div:nth-child(odd) {
      border-right: 0;
    }
  }
}
</style>
